import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import PostService from '../services/postService';
import BurningPageService, {
  BurningEntity,
  BurningPageFilters,
} from '../services/burningPageService';
// import { useAuth } from '../hooks/useAuth'; // Not currently used
import { transformApiPost, type Post } from './PostsContext';
import { entityStore, type User, type Tag } from '../stores/entityStore';
import { feedCache, type FeedCursor } from '../utils/feedCache';
import { requestDeduplication } from '../utils/requestDeduplication';

// Cursor type for burning entities (similar to feed)
interface BurningCursor {
  timestamp: number;
  entityId: string;
}

interface FeedState {
  posts: Post[];
  cursor: FeedCursor | null;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  scrollPosition: number;
  lastPostId: string | null;
  newPostsCount: number;
}

interface BurningState {
  entities: BurningEntity[];
  cursor: BurningCursor | null;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  scrollPosition: number;
  filters: BurningPageFilters;
  currentPage: number; // Keep for backward compatibility
}

interface UnifiedFeedState {
  feed: FeedState;
  burning: BurningState;
}

interface UnifiedFeedContextType {
  state: UnifiedFeedState;
  // Feed methods
  loadFeed: (
    cursor?: FeedCursor | null,
    append?: boolean,
    force?: boolean
  ) => Promise<void>;
  loadMoreFeed: () => Promise<void>;
  refreshFeed: (force?: boolean) => Promise<void>;
  checkForNewPosts: () => Promise<number>;
  showNewPosts: () => Promise<void>;
  // Burning methods
  loadBurning: (
    page?: number,
    append?: boolean,
    force?: boolean
  ) => Promise<void>;
  loadMoreBurning: () => Promise<void>;
  refreshBurning: (force?: boolean) => Promise<void>;
  setBurningFilters: (filters: Partial<BurningPageFilters>) => void;
  // Scroll position
  saveScrollPosition: (type: 'feed' | 'burning', position: number) => void;
  restoreScrollPosition: (type: 'feed' | 'burning') => number;
  // Post actions
  updatePostReaction: (postId: string, reactionType: string) => Promise<void>;
  addPostOptimistically: (post: Post) => void;
  updatePost: (postId: string, updates: Partial<Post>) => void;
  // Comments
  toggleComments: (postId: string) => void;
  expandedComments: Record<string, boolean>;
  setExpandedComments: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  submitComment: (postId: string, content: string) => Promise<void>;
  getComments: (postId: string, page?: number, limit?: number) => Promise<any>;
}

const UnifiedFeedContext = createContext<UnifiedFeedContextType | undefined>(
  undefined
);

// Restore state from sessionStorage
const restoreState = (): UnifiedFeedState | null => {
  try {
    const saved = sessionStorage.getItem('unified-feed-state');
    if (!saved) return null;

    const parsed = JSON.parse(saved);
    const age = Date.now() - (parsed.timestamp || 0);

    // Cache valid for 5 minutes
    if (age > 5 * 60 * 1000) {
      return null; // Cache expired
    }

    // Validate structure
    if (parsed.feed && parsed.burning) {
      return {
        feed: {
          posts: parsed.feed.posts || [],
          cursor: parsed.feed.cursor || null,
          hasMore:
            parsed.feed.hasMore !== undefined ? parsed.feed.hasMore : true,
          isLoading: false,
          isLoadingMore: false,
          scrollPosition: parsed.feed.scrollPosition || 0,
          lastPostId: parsed.feed.lastPostId || null,
          newPostsCount: 0,
        },
        burning: {
          entities: parsed.burning.entities || [],
          cursor: parsed.burning.cursor || null,
          hasMore:
            parsed.burning.hasMore !== undefined
              ? parsed.burning.hasMore
              : true,
          isLoading: false,
          isLoadingMore: false,
          scrollPosition: parsed.burning.scrollPosition || 0,
          filters: parsed.burning.filters || {
            entityType: 'all',
            sortBy: 'heat',
          },
          currentPage: parsed.burning.currentPage || 1,
        },
      };
    }
  } catch (error) {
    console.error('Error restoring feed state:', error);
  }
  return null;
};

// Save state to sessionStorage
const saveState = (state: UnifiedFeedState) => {
  try {
    sessionStorage.setItem(
      'unified-feed-state',
      JSON.stringify({
        ...state,
        timestamp: Date.now(),
      })
    );
  } catch (error) {
    console.error('Error saving feed state:', error);
  }
};

export const UnifiedFeedProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // Note: user is available but not currently used in this context
  // const { user: _user } = useAuth(); // Prefixed with _ to indicate intentionally unused
  const [state, setState] = useState<UnifiedFeedState>(() => {
    const restored = restoreState();
    if (restored) return restored;

    return {
      feed: {
        posts: [],
        cursor: null,
        hasMore: true,
        isLoading: false,
        isLoadingMore: false,
        scrollPosition: 0,
        lastPostId: null,
        newPostsCount: 0,
      },
      burning: {
        entities: [],
        cursor: null,
        hasMore: true,
        isLoading: false,
        isLoadingMore: false,
        scrollPosition: 0,
        filters: {
          entityType: 'all',
          sortBy: 'heat',
        },
        currentPage: 1,
      },
    };
  });

  const [expandedComments, setExpandedComments] = useState<
    Record<string, boolean>
  >({});

  // Prevent duplicate simultaneous loads
  const isLoadingRef = useRef(false);
  const pendingReactions = useRef<Set<string>>(new Set());

  // Retry helper with exponential backoff
  const retryRequest = useCallback(
    async (
      fn: () => Promise<any>,
      maxRetries = 3,
      retryCount = 0
    ): Promise<any> => {
      try {
        return await fn();
      } catch (error: any) {
        // Check if it's a network error or timeout (but NOT rate limiting)
        const isRateLimit = error?.status === 429;
        const isNetworkError =
          !isRateLimit &&
          (error?.message?.includes('Network') ||
            error?.message?.includes('fetch') ||
            error?.message?.includes('Failed to fetch') ||
            error?.message?.includes('timeout') ||
            error?.name === 'TypeError' ||
            (error?.status >= 500 && error?.status < 600));

        if (isNetworkError && retryCount < maxRetries) {
          // Exponential backoff: 1s, 2s, 4s
          const delay = Math.pow(2, retryCount) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));

          // Recursive retry
          return retryRequest(fn, maxRetries, retryCount + 1);
        }

        // Not a network error or max retries reached
        throw error;
      }
    },
    []
  );

  // Persist state to sessionStorage
  useEffect(() => {
    saveState(state);
  }, [state]);

  // Load feed with cursor-based pagination
  const loadFeed = useCallback(
    async (cursor: FeedCursor | null = null, append = false, force = false) => {
      // Prevent concurrent loads
      if (isLoadingRef.current && !append) {
        return;
      }

      if (!append) {
        isLoadingRef.current = true;
        setState(prev => ({
          ...prev,
          feed: { ...prev.feed, isLoading: true },
        }));
      } else {
        setState(prev => ({
          ...prev,
          feed: { ...prev.feed, isLoadingMore: true },
        }));
      }

      try {
        // Check cache first (unless forcing)
        if (!force && cursor) {
          const cached = await feedCache.getCachedBatch(cursor);
          if (cached) {
            // Update entity store
            entityStore.updateEntities({
              posts: cached.posts,
              ...(cached.users && {
                users: cached.users as Record<string, User>,
              }),
              ...(cached.tags && { tags: cached.tags as Record<string, Tag> }),
            });

            setState(prev => ({
              ...prev,
              feed: {
                ...prev.feed,
                posts: append
                  ? [...prev.feed.posts, ...cached.posts]
                  : cached.posts,
                cursor: cached.nextCursor,
                hasMore: cached.hasMore,
                isLoading: false,
                isLoadingMore: false,
              },
            }));

            isLoadingRef.current = false;
            return;
          }
        }

        // Fetch from API
        const cursorToUse = cursor || state.feed.cursor;
        const cacheKey = `feed-${cursorToUse ? JSON.stringify(cursorToUse) : 'initial'}`;

        const response = await requestDeduplication.request(
          cacheKey,
          async () => {
            return await retryRequest(
              async () => {
                return await PostService.getPostsFeed({
                  cursor: cursorToUse,
                  limit: 20,
                  mode: 'chronological',
                  cacheBust: force,
                });
              },
              3 // max retries
            );
          }
        );

        // Update entity store with normalized data
        entityStore.updateEntities({
          posts: response.posts,
          users: response.users,
          tags: response.tags,
        });

        // Cache the batch
        if (response.nextCursor) {
          await feedCache.cacheFeedBatch(response, response.nextCursor);
        }

        const transformedPosts = response.posts.map(transformApiPost);

        setState(prev => ({
          ...prev,
          feed: {
            ...prev.feed,
            posts: append
              ? [...prev.feed.posts, ...transformedPosts]
              : transformedPosts,
            cursor: response.nextCursor || null,
            hasMore: response.hasMore,
            isLoading: false,
            isLoadingMore: false,
            lastPostId:
              transformedPosts.length > 0
                ? transformedPosts[0].id
                : prev.feed.lastPostId,
          },
        }));
      } catch (error) {
        console.error('Error loading feed:', error);
        setState(prev => ({
          ...prev,
          feed: {
            ...prev.feed,
            isLoading: false,
            isLoadingMore: false,
          },
        }));
      } finally {
        isLoadingRef.current = false;
      }
    },
    [state.feed.cursor, retryRequest]
  );

  // Load more feed posts
  const loadMoreFeed = useCallback(async (): Promise<void> => {
    if (!state.feed.isLoadingMore && state.feed.hasMore && state.feed.cursor) {
      await loadFeed(state.feed.cursor, true);
    }
  }, [state.feed, loadFeed]);

  // Refresh feed
  const refreshFeed = useCallback(
    async (force = false) => {
      await loadFeed(null, false, force);
    },
    [loadFeed]
  );

  // Check for new posts (returns count only)
  const checkForNewPosts = useCallback(async (): Promise<number> => {
    const lastPostId = state.feed.lastPostId;
    if (!lastPostId) return 0;

    try {
      // Use cursor if available, otherwise use postId
      const since = state.feed.cursor || lastPostId;
      const response = await PostService.checkForNewPosts(since);
      const count = response.posts?.length || 0;

      setState(prev => ({
        ...prev,
        feed: { ...prev.feed, newPostsCount: count },
      }));

      return count;
    } catch (error) {
      console.error('Error checking for new posts:', error);
      return 0;
    }
  }, [state.feed.lastPostId, state.feed.cursor, retryRequest]);

  // Show new posts (load and prepend)
  const showNewPosts = useCallback(async () => {
    const lastPostId = state.feed.lastPostId;
    if (!lastPostId) {
      await refreshFeed(true);
      return;
    }

    try {
      // Use cursor if available, otherwise use postId
      const since = state.feed.cursor || lastPostId;
      const response = await retryRequest(
        async () => {
          return await PostService.checkForNewPosts(since);
        },
        2 // fewer retries for polling
      );
      const newPosts = response.posts?.map(transformApiPost) || [];

      if (newPosts.length > 0) {
        // Update entity store
        entityStore.updateEntities({
          posts: newPosts,
        });

        setState(prev => ({
          ...prev,
          feed: {
            ...prev.feed,
            posts: [...newPosts, ...prev.feed.posts],
            lastPostId: newPosts[0].id,
            newPostsCount: 0,
            // Update cursor to first new post
            cursor: newPosts[0]
              ? {
                  timestamp: new Date(newPosts[0].timestamp).getTime(),
                  postId: newPosts[0].id,
                }
              : prev.feed.cursor,
          },
        }));
      } else {
        // No new posts, just clear count
        setState(prev => ({
          ...prev,
          feed: { ...prev.feed, newPostsCount: 0 },
        }));
      }
    } catch (error) {
      console.error('Error showing new posts:', error);
      await refreshFeed(true);
    }
  }, [state.feed.lastPostId, state.feed.cursor, refreshFeed, retryRequest]);

  // Load burning entities
  const loadBurning = useCallback(
    async (page = 1, append = false, _force = false) => {
      // Prevent concurrent loads
      if (isLoadingRef.current && !append) {
        return;
      }

      if (!append) {
        isLoadingRef.current = true;
        setState(prev => ({
          ...prev,
          burning: { ...prev.burning, isLoading: true },
        }));
      } else {
        setState(prev => ({
          ...prev,
          burning: { ...prev.burning, isLoadingMore: true },
        }));
      }

      try {
        const cacheKey = `burning-${JSON.stringify(state.burning.filters)}-${page}`;
        const response = await requestDeduplication.request(
          cacheKey,
          async () => {
            return await retryRequest(
              async () => {
                return await BurningPageService.getBurningEntities({
                  ...state.burning.filters,
                  page,
                  limit: 20,
                });
              },
              2 // max retries (fewer for burning page)
            );
          }
        );

        setState(prev => ({
          ...prev,
          burning: {
            ...prev.burning,
            entities: append
              ? [...prev.burning.entities, ...response.entities]
              : response.entities,
            hasMore: response.pagination.hasMore,
            isLoading: false,
            isLoadingMore: false,
            currentPage: page,
          },
        }));
      } catch (error) {
        console.error('Error loading burning entities:', error);
        setState(prev => ({
          ...prev,
          burning: {
            ...prev.burning,
            isLoading: false,
            isLoadingMore: false,
          },
        }));
      } finally {
        if (!append) {
          isLoadingRef.current = false;
        }
      }
    },
    [state.burning.filters, retryRequest]
  );

  // Load more burning entities
  const loadMoreBurning = useCallback(async (): Promise<void> => {
    if (!state.burning.isLoadingMore && state.burning.hasMore) {
      await loadBurning(state.burning.currentPage + 1, true);
    }
  }, [state.burning, loadBurning]);

  // Refresh burning
  const refreshBurning = useCallback(
    async (force = false) => {
      await loadBurning(1, false, force);
    },
    [loadBurning]
  );

  // Set burning filters
  const setBurningFilters = useCallback(
    (filters: Partial<BurningPageFilters>) => {
      setState(prev => {
        const newFilters = { ...prev.burning.filters, ...filters };
        // Only update if filters actually changed
        const filtersChanged =
          JSON.stringify(newFilters) !== JSON.stringify(prev.burning.filters);

        if (!filtersChanged) {
          return prev; // No change, return previous state
        }

        return {
          ...prev,
          burning: {
            ...prev.burning,
            filters: newFilters,
            entities: [], // Clear entities when filters change
            cursor: null,
          },
        };
      });
      // Don't call loadBurning here - let the useEffect in HomePage handle it
      // This prevents duplicate calls
    },
    []
  );

  // Save scroll position
  const saveScrollPosition = useCallback(
    (type: 'feed' | 'burning', position: number) => {
      setState(prev => ({
        ...prev,
        [type]: { ...prev[type], scrollPosition: position },
      }));
    },
    []
  );

  // Restore scroll position
  const restoreScrollPosition = useCallback(
    (type: 'feed' | 'burning') => {
      return state[type].scrollPosition;
    },
    [state]
  );

  // Update post reaction
  const updatePostReaction = useCallback(
    async (postId: string, reactionType: string) => {
      if (pendingReactions.current.has(postId)) {
        return; // Already processing
      }

      pendingReactions.current.add(postId);

      try {
        await PostService.reactToPost(postId, {
          type: reactionType as any,
        });

        // Optimistically update UI
        setState(prev => ({
          ...prev,
          feed: {
            ...prev.feed,
            posts: prev.feed.posts.map(post => {
              if (post.id === postId) {
                const currentReactions = { ...post.reactions };
                const currentUserReaction = post.userReaction;

                // Toggle logic: if same reaction, remove it; otherwise, add it
                if (currentUserReaction === reactionType) {
                  // Remove reaction
                  currentReactions[reactionType.toLowerCase()] =
                    (currentReactions[reactionType.toLowerCase()] || 1) - 1;
                  return {
                    ...post,
                    reactions: currentReactions,
                    userReaction: undefined,
                  };
                } else {
                  // Add new reaction
                  if (currentUserReaction) {
                    currentReactions[currentUserReaction.toLowerCase()] =
                      (currentReactions[currentUserReaction.toLowerCase()] ||
                        1) - 1;
                  }
                  currentReactions[reactionType.toLowerCase()] =
                    (currentReactions[reactionType.toLowerCase()] || 0) + 1;
                  return {
                    ...post,
                    reactions: currentReactions,
                    userReaction: reactionType,
                  };
                }
              }
              return post;
            }),
          },
        }));
      } catch (error) {
        console.error('Error updating post reaction:', error);
      } finally {
        pendingReactions.current.delete(postId);
      }
    },
    []
  );

  // Add post optimistically
  const addPostOptimistically = useCallback((post: Post) => {
    setState(prev => ({
      ...prev,
      feed: {
        ...prev.feed,
        posts: [post, ...prev.feed.posts],
        lastPostId: post.id,
      },
    }));
    entityStore.updateEntities({ posts: [post] });
  }, []);

  // Update post
  const updatePost = useCallback((postId: string, updates: Partial<Post>) => {
    setState(prev => ({
      ...prev,
      feed: {
        ...prev.feed,
        posts: prev.feed.posts.map(post =>
          post.id === postId ? { ...post, ...updates } : post
        ),
      },
    }));
    entityStore.updatePost(postId, updates);
  }, []);

  // Toggle comments
  const toggleComments = useCallback((postId: string) => {
    setExpandedComments(prev => ({
      ...prev,
      [postId]: !prev[postId],
    }));
  }, []);

  // Submit comment
  const submitComment = useCallback(
    async (postId: string, content: string) => {
      try {
        await PostService.addComment(postId, { content });
        // Refresh posts to get updated comment count
        await refreshFeed();
      } catch (error) {
        console.error('Error submitting comment:', error);
        throw error;
      }
    },
    [refreshFeed]
  );

  // Get comments
  const getComments = useCallback(
    async (postId: string, page = 1, limit = 20) => {
      try {
        return await PostService.getComments(postId, page, limit);
      } catch (error) {
        console.error('Failed to fetch comments:', error);
        throw error;
      }
    },
    []
  );

  const value: UnifiedFeedContextType = {
    state,
    loadFeed,
    loadMoreFeed,
    refreshFeed,
    checkForNewPosts,
    showNewPosts,
    loadBurning,
    loadMoreBurning,
    refreshBurning,
    setBurningFilters,
    saveScrollPosition,
    restoreScrollPosition,
    updatePostReaction,
    addPostOptimistically,
    updatePost,
    toggleComments,
    expandedComments,
    setExpandedComments,
    submitComment,
    getComments,
  };

  return (
    <UnifiedFeedContext.Provider value={value}>
      {children}
    </UnifiedFeedContext.Provider>
  );
};

export const useUnifiedFeed = (): UnifiedFeedContextType => {
  const context = useContext(UnifiedFeedContext);
  if (!context) {
    throw new Error('useUnifiedFeed must be used within UnifiedFeedProvider');
  }
  return context;
};
